import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, onSnapshot, deleteDoc, doc } from 'firebase/firestore';

const MEDALS = ['🥇', '🥈', '🥉'];

function formatDate(id) {
  const ts = parseInt(id, 10);
  if (isNaN(ts)) return '';
  return new Date(ts).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export default function Leaderboard() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const loadGuestSubcollection = async (event) => {
      if (Array.isArray(event.guests)) {
        return event;
      }

      try {
        const guestSnapshot = await getDocs(collection(db, 'events', event.id, 'guests'));
        const guests = guestSnapshot.docs.map((doc) => doc.data());
        return { ...event, guests };
      } catch (err) {
        console.warn('Unable to load guest subcollection for event', event.id, err);
        return { ...event, guests: [] };
      }
    };

    const unsub = onSnapshot(
      collection(db, 'events'),
      async (snapshot) => {
        const list = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const event = { id: doc.id, ...doc.data() };
            return loadGuestSubcollection(event);
          })
        );
        setEvents(list);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setError('Could not load events. Check Firestore rules or connection.');
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const deleteGuest = async (eventId, guestEmail) => {
    if (!window.confirm(`Are you sure you want to delete this player?`)) {
      return;
    }

    try {
      // Get the guests subcollection and find the matching guest document
      const guestSnapshot = await getDocs(collection(db, 'events', eventId, 'guests'));
      const guestDoc = guestSnapshot.docs.find((doc) => doc.data().email === guestEmail);

      if (guestDoc) {
        await deleteDoc(doc(db, 'events', eventId, 'guests', guestDoc.id));
        console.log(`Deleted guest ${guestEmail} from event ${eventId}`);
      }
    } catch (err) {
      console.error('Failed to delete guest:', err);
      alert('Failed to delete player. Please try again.');
    }
  };

  return (
    <section id="leaderboard" className="leaderboard-section">
      <div className="container">
        <div className="leaderboard-intro">
          <p className="section-label">Live Results</p>
          <h2 className="section-title">Event <span>Leaderboards</span></h2>
          <p>Select an event below to see the full leaderboard.</p>
        </div>

        {loading && (
          <div className="lb-status">
            <div className="lb-spinner" />
            <span>Loading events…</span>
          </div>
        )}

        {error && <div className="lb-error">{error}</div>}

        {!loading && !error && events.length === 0 && (
          <div className="lb-status">No events found.</div>
        )}

        <div className="lb-events">
          {events.map((event) => {
            const guests = event.guests || [];
            const sorted = [...guests].sort((a, b) => b.score - a.score);
            const isOpen = expandedId === event.id;
            const dateStr = formatDate(event.id);

            return (
              <div key={event.id} className={`lb-card${isOpen ? ' lb-card--open' : ''}`}>
                <button className="lb-card-header" onClick={() => toggle(event.id)}>
                  <div className="lb-card-meta">
                    <span className="lb-event-name">{event.eventName}</span>
                    {dateStr && <span className="lb-event-date">{dateStr}</span>}
                  </div>
                  <div className="lb-card-right">
                    <span className="lb-player-count">{guests.length} players</span>
                    <svg className="lb-chevron" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </button>

                {isOpen && (
                  <div className="lb-table-wrap">
                    {sorted.length === 0 ? (
                      <p className="lb-no-scores">No scores recorded yet.</p>
                    ) : (
                      <table className="lb-table">
                        <thead>
                          <tr>
                            <th className="lb-th-rank">Rank</th>
                            <th className="lb-th-name">Player</th>
                            <th className="lb-th-score">Score</th>
                            <th className="lb-th-action">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sorted.map((guest, i) => (
                            <tr
                              key={guest.email + i}
                              className={
                                i === 0 ? 'lb-row lb-row--gold'
                                : i === 1 ? 'lb-row lb-row--silver'
                                : i === 2 ? 'lb-row lb-row--bronze'
                                : 'lb-row'
                              }
                            >
                              <td className="lb-td-rank">
                                {i < 3
                                  ? <span className="lb-medal">{MEDALS[i]}</span>
                                  : <span className="lb-rank-num">{i + 1}</span>}
                              </td>
                              <td className="lb-td-name">{guest.name}</td>
                              <td className="lb-td-score">{guest.score}</td>
                              <td className="lb-td-action">
                                <button
                                  onClick={() => deleteGuest(event.id, guest.email)}
                                  className="lb-delete-btn"
                                  title="Delete player"
                                  aria-label="Delete player"
                                >
                                  ✕
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
